import { scopeOf, type FlagStore } from "./cache";
import type { FlagServiceClient } from "./client";
import type {
  WireCapability,
  WireCapabilitySet,
  WireEvaluationContext,
  WireGetCapabilitiesResponse,
} from "./wire";

/**
 * A resolved capability, normalized from the wire shape so consumers get
 * non-optional fields. Gate UI/behavior on {@link Capability.enabled}; read
 * {@link Capability.value} for value-carrying capabilities (tier/limit/config).
 *
 * This is the result of resolving a capability for the caller's tenant — never
 * the backing flag or its targeting rules.
 */
export interface Capability {
  /** Whether the capability is on for the resolved tenant/context. */
  enabled: boolean;
  /** Resolved value as native JSON (the boolean itself for a plain capability). */
  value: unknown;
  /** The variation key the capability resolved to (diagnostic). */
  variation: string;
}

/**
 * Capability keys known to this SDK build. The authoritative catalog is
 * server-side (the flagservice `capability_definitions` table) and open-ended;
 * this list only powers editor autocomplete and guarantees the keys are present
 * in every normalized set. A key missing here still works everywhere.
 */
export const knownCapabilityKeys = [
  "analytics",
  "websocket",
  "settings-manager",
] as const;

/** A capability key known at SDK build time (see {@link knownCapabilityKeys}). */
export type KnownCapabilityKey = (typeof knownCapabilityKeys)[number];

/**
 * Any capability key. Known keys autocomplete; any other string is accepted,
 * since the capability catalog is server-defined and open-ended. (`string & {}`
 * keeps the literal suggestions from collapsing into plain `string`.)
 */
export type CapabilityKey = KnownCapabilityKey | (string & {});

/**
 * Resolved capabilities, keyed by capability name. Every key the server
 * returned is present, and the {@link knownCapabilityKeys} are always present.
 */
export type CapabilitySet = Record<string, Capability>;

/** The value used for a capability that is absent or switched off. */
export const disabledCapability: Capability = {
  enabled: false,
  value: null,
  variation: "",
};

/**
 * Resolve the caller's capability set, through a {@link FlagStore} when one is
 * supplied — so a fresh cached snapshot satisfies the call with no network and
 * concurrent callers de-dupe, exactly like the flags track. Snapshots are kept
 * in a separate `"capabilities"` bucket, scoped by environment + context. With
 * no store it goes straight to the client (the original behavior).
 */
export function loadCapabilities(
  client: FlagServiceClient,
  store: FlagStore | undefined,
  envId: string,
  context?: WireEvaluationContext,
): Promise<WireGetCapabilitiesResponse> {
  const fetcher = () => client.getCapabilities(context);
  if (!store) {
    return fetcher();
  }
  return store.load(
    scopeOf({ envId, context: context ?? {}, track: "capabilities" }),
    fetcher,
  );
}

/** Normalize one wire capability, filling defaults for absent fields. */
export function normalizeCapability(wire: WireCapability | undefined): Capability {
  if (!wire) {
    return disabledCapability;
  }
  return {
    enabled: wire.enabled ?? false,
    value: wire.value ?? null,
    variation: wire.variation ?? "",
  };
}

/**
 * Normalize the wire capability set into a fully-populated map. The capability
 * catalog is server-defined, so every key in the response flows through — an
 * SDK build never drops a capability it doesn't know about. The
 * {@link knownCapabilityKeys} are additionally always present, filled disabled
 * when absent from the response.
 */
export function normalizeCapabilitySet(
  wire: WireCapabilitySet | undefined,
): CapabilitySet {
  const set: CapabilitySet = {};
  for (const key of knownCapabilityKeys) {
    set[key] = disabledCapability;
  }
  for (const [key, capability] of Object.entries(wire ?? {})) {
    set[key] = normalizeCapability(capability);
  }
  return set;
}
